import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { buildVoiceFileName } from './utils';

const getUploadErrorKey = (error) => (
  error?.message === 'Failed to create chat media'
    ? 'Informative.Negative.voiceMessageCreateFailed'
    : 'Informative.Negative.voiceMessageUploadFailed'
);

export const useVoiceMessageComposer = ({
  t,
  isAreaDisabled,
  hasText,
  createVoiceFile,
  sendVoiceMessage,
  onVoiceDraftStateChange,
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isUploadingVoice, setIsUploadingVoice] = useState(false);
  const [recordingDurationSeconds, setRecordingDurationSeconds] = useState(0);
  const [voicePreviewUrl, setVoicePreviewUrl] = useState('');
  const [voiceBlob, setVoiceBlob] = useState(null);
  const [voiceMimeType, setVoiceMimeType] = useState('');
  const [voiceError, setVoiceError] = useState('');

  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const recordingStreamRef = useRef(null);
  const recordingTimerRef = useRef(null);
  const isUnmountedRef = useRef(false);

  const clearRecordingTimer = useCallback(() => {
    if (recordingTimerRef.current) {
      window.clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
  }, []);

  const stopRecordingStream = useCallback(() => {
    if (recordingStreamRef.current) {
      recordingStreamRef.current.getTracks().forEach((track) => track.stop());
      recordingStreamRef.current = null;
    }
  }, []);

  const revokeVoicePreview = useCallback(() => {
    if (voicePreviewUrl) {
      URL.revokeObjectURL(voicePreviewUrl);
    }
  }, [voicePreviewUrl]);

  const hasVoiceDraft = useMemo(() => Boolean(voiceBlob && voicePreviewUrl), [voiceBlob, voicePreviewUrl]);
  const isVoiceModeActive = isRecording || hasVoiceDraft;

  const notifyVoiceState = useCallback((isActive) => {
    onVoiceDraftStateChange?.(isActive);
  }, [onVoiceDraftStateChange]);

  const resetVoiceDraft = useCallback(() => {
    clearRecordingTimer();
    stopRecordingStream();
    revokeVoicePreview();
    mediaRecorderRef.current = null;
    recordedChunksRef.current = [];

    setIsRecording(false);
    setIsUploadingVoice(false);
    setRecordingDurationSeconds(0);
    setVoicePreviewUrl('');
    setVoiceBlob(null);
    setVoiceMimeType('');
    setVoiceError('');

    notifyVoiceState(false);
  }, [clearRecordingTimer, notifyVoiceState, revokeVoicePreview, stopRecordingStream]);

  const startRecordingTimer = useCallback(() => {
    clearRecordingTimer();
    recordingTimerRef.current = window.setInterval(() => {
      setRecordingDurationSeconds((prev) => prev + 1);
    }, 1000);
  }, [clearRecordingTimer]);

  const handleStartRecording = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia || typeof window.MediaRecorder === 'undefined') {
      setVoiceError(t('Informative.Negative.voiceMessageUnavailable'));
      return;
    }

    revokeVoicePreview();
    recordedChunksRef.current = [];

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = window.MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : '';
      const mediaRecorder = new window.MediaRecorder(stream, mimeType ? { mimeType } : undefined);

      recordingStreamRef.current = stream;
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          recordedChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        clearRecordingTimer();
        stopRecordingStream();
        setIsRecording(false);
        try {
          if (recordedChunksRef.current.length === 0) {
            if (isUnmountedRef.current) {
              return;
            }
            setVoiceError(t('Informative.Negative.voiceMessageEmpty'));
            notifyVoiceState(false);
            return;
          }

          const resolvedMimeType = mediaRecorder.mimeType || 'audio/webm';
          const previewBlob = new Blob(recordedChunksRef.current, { type: resolvedMimeType });
          const nextPreviewUrl = URL.createObjectURL(previewBlob);

          if (isUnmountedRef.current) {
            URL.revokeObjectURL(nextPreviewUrl);
            return;
          }

          setVoiceError('');
          setVoiceBlob(previewBlob);
          setVoiceMimeType(resolvedMimeType);
          setVoicePreviewUrl(nextPreviewUrl);
          notifyVoiceState(true);
        } catch (error) {
          if (!isUnmountedRef.current) {
            setVoiceError(t('Informative.Negative.voiceMessageUploadFailed'));
            notifyVoiceState(false);
          }
        } finally {
          mediaRecorderRef.current = null;
        }
      };

      mediaRecorder.start();
      setIsRecording(true);
      setIsUploadingVoice(false);
      setRecordingDurationSeconds(0);
      setVoicePreviewUrl('');
      setVoiceBlob(null);
      setVoiceMimeType('');
      setVoiceError('');
      notifyVoiceState(true);
      startRecordingTimer();
    } catch (error) {
      stopRecordingStream();
      if (isUnmountedRef.current) {
        return;
      }
      setVoiceError(t('Informative.Negative.voiceMessagePermissionDenied'));
      notifyVoiceState(false);
    }
  }, [
    clearRecordingTimer,
    notifyVoiceState,
    revokeVoicePreview,
    startRecordingTimer,
    stopRecordingStream,
    t,
  ]);

  const handleStopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
  }, []);

  const handleCancelVoice = useCallback(() => {
    if (isRecording) {
      handleStopRecording();
      window.setTimeout(() => {
        resetVoiceDraft();
      }, 0);
      return;
    }

    resetVoiceDraft();
  }, [handleStopRecording, isRecording, resetVoiceDraft]);

  const handleSendVoice = useCallback(async () => {
    if (!voiceBlob) {
      return;
    }

    setIsUploadingVoice(true);
    setVoiceError('');

    try {
      const fileName = buildVoiceFileName(voiceMimeType || voiceBlob.type);
      const file = new File([voiceBlob], fileName, {
        type: voiceMimeType || voiceBlob.type || 'audio/webm',
      });

      const voiceAttachment = await createVoiceFile({
        file,
        fileName,
        recordingDurationSeconds,
      });

      await sendVoiceMessage({
        voiceAttachment,
        recordingDurationSeconds,
      });

      resetVoiceDraft();
    } catch (error) {
      console.error('Voice message send failed', error);
      if (isUnmountedRef.current) {
        return;
      }
      setIsUploadingVoice(false);
      setVoiceError(t(getUploadErrorKey(error)));
    }
  }, [
    createVoiceFile,
    recordingDurationSeconds,
    resetVoiceDraft,
    sendVoiceMessage,
    t,
    voiceBlob,
    voiceMimeType,
  ]);

  const voiceActionState = useMemo(() => {
    let voiceActionLabel = t('Actions.Instance.recordVoice');
    let voiceActionHandler = handleStartRecording;
    let voiceActionDisabled = isAreaDisabled || isUploadingVoice;
    let isVoiceActionDanger = false;

    if (isRecording) {
      voiceActionLabel = t('Actions.Instance.stopRecording');
      voiceActionHandler = handleStopRecording;
      isVoiceActionDanger = true;
    } else if (hasVoiceDraft) {
      voiceActionLabel = t('Actions.Instance.sendVoice');
      voiceActionHandler = handleSendVoice;
    } else if (hasText) {
      voiceActionDisabled = true;
    }

    const voiceActionTitle = hasText && !isVoiceModeActive
      ? t('Informative.Notifying.clearTextToRecord')
      : voiceActionLabel;

    return {
      voiceActionLabel,
      voiceActionHandler,
      voiceActionDisabled,
      isVoiceActionDanger,
      voiceActionTitle,
    };
  }, [
    t,
    handleStartRecording,
    isAreaDisabled,
    isUploadingVoice,
    isRecording,
    handleStopRecording,
    hasVoiceDraft,
    handleSendVoice,
    hasText,
    isVoiceModeActive,
  ]);

  useEffect(() => () => {
    isUnmountedRef.current = true;
    clearRecordingTimer();
    stopRecordingStream();
  }, [clearRecordingTimer, stopRecordingStream]);

  return {
    isRecording,
    isUploadingVoice,
    recordingDurationSeconds,
    voicePreviewUrl,
    voiceError,
    hasVoiceDraft,
    isVoiceModeActive,
    voiceActionState,
    handleCancelVoice,
  };
};

export default useVoiceMessageComposer;
